import React from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    isDestructive?: boolean;
    isLoading?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    isDestructive = false,
    isLoading = false
}) => {
    if (!isOpen) return null;

    const handleConfirm = () => {
        onConfirm();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            onClose();
        }
    };

    return (
        <div 
            className="modal-overlay"
            onClick={onClose}
            onKeyDown={handleKeyDown}
        >
            <div 
                className="modal-content relative"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Close"
                    disabled={isLoading}
                >
                    <XMarkIcon className="h-6 w-6" />
                </button>

                {/* Icon and Title */}
                <div className="flex items-start space-x-3 mb-4 pr-8">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        isDestructive 
                            ? 'bg-red-100 text-red-600' 
                            : 'bg-green-100 text-green-600'
                    }`}>
                        <ExclamationTriangleIcon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900">
                            {title}
                        </h3>
                    </div>
                </div>

                {/* Message */}
                <div className="mb-6">
                    <p className="dfo-text-body">
                        {message.split('**').map((part, index) => 
                            index % 2 === 1 ? (
                                <span key={index} className="font-semibold text-white dfo-bg-gradient-green px-2 py-1 rounded">
                                    {part}
                                </span>
                            ) : (
                                part
                            )
                        )}
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        disabled={isLoading}
                        className="btn-secondary"
                    >
                        {cancelText}
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isLoading}
                        className={isDestructive ? "btn-danger" : "btn-primary"}
                    >
                        {isLoading ? (
                            <div className="flex items-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Processing...
                            </div>
                        ) : (
                            confirmText
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmationModal;
